import { useSyncExternalStore } from 'react';

/**
 * Appearance: `localStorage['circles.theme']` ∈ system | light | dark (default light), shared
 * with the runtime SDK (architecture §10). The inline script in index.html applies it before
 * first paint; this module keeps <html data-theme> in sync afterwards.
 */
export type ThemePref = 'system' | 'light' | 'dark';
export type Theme = 'light' | 'dark';

export const THEME_KEY = 'circles.theme';
export const DEFAULT_THEME: ThemePref = 'light';
/** Must match the --bg tokens in styles.css and the inline script in index.html. */
export const THEME_COLOR: Record<Theme, string> = { light: '#f8f6f2', dark: '#1b1915' };

const listeners = new Set<() => void>();
const dark = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;

export function parseThemePref(value: string | null | undefined): ThemePref {
  return value === 'system' || value === 'light' || value === 'dark' ? value : DEFAULT_THEME;
}

export function getThemePref(): ThemePref {
  try {
    return parseThemePref(window.localStorage.getItem(THEME_KEY));
  } catch {
    return DEFAULT_THEME;
  }
}

export function resolveTheme(pref: ThemePref, systemDark = dark?.matches ?? false): Theme {
  return pref === 'system' ? (systemDark ? 'dark' : 'light') : pref;
}

export function applyTheme(pref: ThemePref = getThemePref()): void {
  const theme = resolveTheme(pref);
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = THEME_COLOR[theme];
}

export function setThemePref(pref: ThemePref): void {
  try {
    window.localStorage.setItem(THEME_KEY, pref);
  } catch {
    // storage unavailable: applies to this page only
  }
  const apply = () => {
    applyTheme(pref);
    listeners.forEach((l) => l());
  };
  const changes = resolveTheme(pref) !== document.documentElement.dataset.theme;
  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (changes && !still && typeof document.startViewTransition === 'function') document.startViewTransition(apply);
  else apply();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Follows OS changes (for "system") and other tabs; call once at startup. */
export function watchTheme(): void {
  dark?.addEventListener('change', () => {
    if (getThemePref() === 'system') applyTheme('system');
  });
  window.addEventListener('storage', (e) => {
    if (e.key !== THEME_KEY && e.key !== null) return;
    applyTheme();
    listeners.forEach((l) => l());
  });
  applyTheme();
}

export function useThemePref(): [ThemePref, (pref: ThemePref) => void] {
  const pref = useSyncExternalStore(subscribe, getThemePref, () => DEFAULT_THEME);
  return [pref, setThemePref];
}
